import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMAGES } from "../data/content";

const reveal = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const ParallaxImage = ({ src, alt }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="clip-frame group relative h-[60vh] md:h-full min-h-[420px] rounded-sm">
      <motion.img
        style={{ y, scale: 1.18 }}
        src={src}
        alt={alt}
        className="img-zoom absolute inset-0 h-full w-full object-cover will-change-transform"
      />
      <div className="absolute inset-0 bg-[#171B26]/5" />
    </div>
  );
};

export const Chapter = ({ chapter, index, chaptersLabel }) => {
  const hasImage = Boolean(chapter.image);
  const imageLeft = index % 2 === 1;

  return (
    <section
      data-testid={`chapter-${chapter.num}`}
      className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 border-t border-[#E6E3DC]"
    >
      <div className={`grid grid-cols-1 gap-10 lg:gap-16 ${hasImage ? "lg:grid-cols-12" : "lg:grid-cols-1"}`}>
        <div className={`${hasImage ? "lg:col-span-6" : "max-w-3xl"} ${imageLeft ? "lg:order-2" : "lg:order-1"}`}>
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="font-serif-display text-[#B0863A] text-3xl">{chapter.num}</span>
              <span className="h-px flex-1 max-w-[80px] bg-[#E6E3DC]" />
              <span className="overline text-[#7A8091]">{chaptersLabel}</span>
            </div>
            <h2 className="font-serif-display italic font-light text-[#171B26] text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl">
              {chapter.title}
            </h2>
            <p className="mt-6 text-[#171B26] text-lg md:text-xl font-light leading-relaxed max-w-2xl">
              {chapter.lead}
            </p>
          </motion.div>

          {chapter.body && (
            <div className="mt-6 space-y-5 max-w-2xl">
              {chapter.body.map((para, i) => (
                <motion.p
                  key={i}
                  custom={i + 1}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  className="text-[#565C6B] text-base md:text-lg font-light leading-relaxed"
                >
                  {para}
                </motion.p>
              ))}
            </div>
          )}
        </div>

        {hasImage && (
          <div className={`lg:col-span-6 ${imageLeft ? "lg:order-1" : "lg:order-2"}`}>
            <div className="lg:sticky lg:top-24 h-[60vh] lg:h-[78vh]">
              <ParallaxImage src={IMAGES[chapter.image]} alt={chapter.alt} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
